import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { useTourPastSearches } from '@hooks/useTourPastSearches';
import STATIC_ROUTES from '@data/staticRoutes.json';
import SearchMonthDate from '@components/ui/search/SearchMonthDate';
import { YearTabProps } from '@components/ui/search/SearchMonthDate/interface';
import useSearchBar from '@contexts/searchbar';
import { TourSearchBarActionType } from '@contexts/searchbar/types/tour';
import { useAppRouter } from '@contexts/router';
import { useBreakpoint } from '@contexts/breakpoints';
import Loader from '@components/Loader/Loader';

import TourSelection from './tourSelection';
import {
  StyledButton,
  StyledLayout,
  StyledContent,
  Element,
  InputElement,
} from './styled';

export type ITourSearchBarHandle = {
  triggerHandleSubmit: () => void;
};

interface ITourSearchBar {
  isListing?: boolean;
  shouldShallowPush?: boolean;
  onSubmit?(): void;
  alignVertical?: boolean;
  setIsRedirecting?: Dispatch<SetStateAction<boolean>>;
}

const TourSearchBar = forwardRef<ITourSearchBarHandle, ITourSearchBar>(
  ({ isListing = false, onSubmit, alignVertical, setIsRedirecting }, ref) => {
    const { push, isLoading } = useAppRouter();
    const { xl } = useBreakpoint();

    const [isSubmit, setIsSubmit] = useState(false);

    const {
      state: { tour: state },
      dispatch,
    } = useSearchBar();

    const buttonDisabled = !state.searchData?.url;
    const {
      pastTourItems,
      clearPastTourSearch,
      removePastTourSearch,
      saveTourSearch,
    } = useTourPastSearches();

    /**
     * This function is used to trigger the submit button from outside the component.
     * It is used in the drawer search bar in the hotel listing mobile header.
     */
    useImperativeHandle(ref, () => ({
      triggerHandleSubmit() {
        handleSubmit();
      },
    }));

    /**
     * This function is used to check the form states.
     * @returns {boolean} Returns true if the form is valid.
     */
    const checkFormState = () => {
      if (!state.searchData) {
        dispatch({
          type: TourSearchBarActionType.SET_IS_SEARCH_DATA_ERROR,
          payload: true,
        });
      }

      return state.searchData;
    };

    /**
     * This function is used to trigger submit button.
     */

    const handleSubmit = () => {
      const isValid = checkFormState();

      if (isValid) {
        saveTourSearch(state.searchData);
        setIsRedirecting?.(true);

        const prefix = STATIC_ROUTES.tourPathPrefix;
        const hasPrefix = state.href?.includes(prefix);
        const fallbackUrl = `${prefix}/${state?.searchData?.url ?? ''}`;

        const href = state.href
          ? hasPrefix
            ? state.href
            : `${prefix}/${state.href}`
          : fallbackUrl;

        push(href);

        onSubmit?.();
      }
      setIsSubmit(true);
    };

    const resetIsSubmit = () => {
      setIsSubmit(false);
    };

    const onHrefUpdate = (href: string) => {
      dispatch({ type: TourSearchBarActionType.SET_HREF, payload: href });
    };

    const onDateMonthsUpdated = (info: YearTabProps[]) => {
      const [currentYear, nextYear] = info;

      const selectedItems: string[] = [];
      currentYear.months.forEach((item) => {
        if (item.checked) {
          selectedItems.push(`${currentYear.year}${item.number}`);
        }
      });

      nextYear.months.forEach((item) => {
        if (item.checked) {
          selectedItems.push(`${nextYear.year}${item.number}`);
        }
      });
      const currentHref = state.searchData?.url?.split('?')?.[0];
      if (selectedItems.length === 0) return onHrefUpdate?.(currentHref || '');

      onHrefUpdate?.(
        `${
          STATIC_ROUTES.tourPathPrefix
        }/${currentHref}?date=${selectedItems.join('&date=')}`
      );
    };

    useEffect(() => {
      const baseUrl = state.href.split('?')?.[0];
      const selectedItems = state.href.split('?')?.[1];

      if (state.searchData?.url && !!baseUrl) {
        onHrefUpdate(
          `${STATIC_ROUTES.tourPathPrefix}/${state.searchData.url}${
            selectedItems ? `?${selectedItems}` : ''
          }`
        );
      }
    }, [state.searchData?.url]);

    const handleTourSelectionChange = (value) => {
      if (state.isSearchDataError) {
        dispatch({
          type: TourSearchBarActionType.SET_IS_SEARCH_DATA_ERROR,
          payload: false,
        });
      }
      dispatch({
        type: TourSearchBarActionType.SET_SEARCH_DATA,
        payload: value,
      });
    };

    return (
      <StyledLayout $alignVertical={alignVertical} isListing={isListing}>
        <StyledContent alignVertical={alignVertical}>
          <InputElement isListing={isListing} alignVertical={alignVertical}>
            <TourSelection
              onChange={handleTourSelectionChange}
              isSubmit={isSubmit}
              resetIsSubmit={resetIsSubmit}
              pastTourItems={pastTourItems}
              clearPastTourSearch={clearPastTourSearch}
              removePastTourSearch={removePastTourSearch}
            />
          </InputElement>

          <InputElement isListing={isListing} alignVertical={alignVertical}>
            <SearchMonthDate
              onMonthsUpdated={onDateMonthsUpdated}
              inputComponent='shopAndFly'
            />
          </InputElement>

          {xl || !isListing ? (
            <Element>
              <StyledButton
                type={isListing ? 'secondary' : 'primary'}
                block
                disabled={buttonDisabled}
                onClick={handleSubmit}
                data-testid='tourSearchButton'
                alignVertical={alignVertical}
              >
                {isListing ? 'Yeniden' : 'Tur'} Ara
              </StyledButton>
            </Element>
          ) : null}
        </StyledContent>
        {!isListing && isLoading && <Loader />}
      </StyledLayout>
    );
  }
);

export default TourSearchBar;
